import React, { useState, useRef } from 'react';
import {
    Box, Typography, Grid, Button, IconButton, Divider, Switch, 
    TextField, Avatar, Stack, MenuItem, Chip, Select, FormControl,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
    Person as PersonIcon,
    Security as SecurityIcon,
    Notifications as NotificationsIcon,
    CameraAlt as CameraIcon,
    Key as KeyIcon,
    Devices as DevicesIcon,
    AdminPanelSettings as ShieldIcon,
    Visibility,
    VisibilityOff
} from '@mui/icons-material';

const AdminProfileSettings = () => {
    // --- State Management ---
    const [activeTab, setActiveTab] = useState('profile');
    const [openPasswordModal, setOpenPasswordModal] = useState(false);
    const [showPasswords, setShowPasswords] = useState(false);
    
    const [adminData, setAdminData] = useState({
        fullName: localStorage.getItem('userName') || 'System Admin',
        email: 'admin@gtpl.com',
        role: 'Super Administrator',
        phone: '+91 98765 43210',
        twoFactor: true,
        emailAlerts: true,
        browserNotif: false,
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [profileImage, setProfileImage] = useState(
        localStorage.getItem('adminProfileImage') || ""
    );

    const fileInputRef = useRef(null);

    // --- Handlers ---
    const handleInputChange = (field, value) => {
        setAdminData(prev => ({ ...prev, [field]: value }));
    };

    const handlePasswordInputChange = (field, value) => {
        setPasswordData(prev => ({ ...prev, [field]: value }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfileImage(reader.result);
                localStorage.setItem('adminProfileImage', reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveProfile = () => {
        alert('Profile settings updated successfully!');
        localStorage.setItem('userName', adminData.fullName);
    };

    const handleUpdatePassword = () => {
        if (!passwordData.currentPassword || !passwordData.newPassword) {
            alert("Please fill in all fields.");
            return;
        }
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            alert("New passwords do not match!");
            return;
        }
        // Logic for API call would go here
        alert("Password updated successfully!");
        setOpenPasswordModal(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    };

    // --- Styles ---
    const vibrantButtonStyle = {
        borderRadius: '6px',
        textTransform: 'none',
        fontWeight: 600,
        fontFamily: 'Poppins',
        fontSize: '0.75rem',
        py: 0.5,
        px: 2,
    };

    // --- Renderers ---
    const renderProfile = () => (
        <Box sx={{ animation: 'fadeIn 0.5s ease' }}>
            <Typography variant="h6" sx={{ fontFamily: 'Poppins', fontWeight: 700, mb: 1, color: '#1e293b' }}>
                Account Information
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'Roboto', color: '#64748b', mb: 3 }}>
                Update your personal details and public profile presence.
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth label="Full Name" variant="standard"
                        value={adminData.fullName}
                        onChange={(e) => handleInputChange('fullName', e.target.value)}
                        InputLabelProps={{ shrink: true, sx: { fontFamily: 'Poppins', fontWeight: 600 } }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth label="Email Address" variant="standard"
                        value={adminData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        InputLabelProps={{ shrink: true, sx: { fontFamily: 'Poppins', fontWeight: 600 } }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth label="Phone Number" variant="standard"
                        value={adminData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        InputLabelProps={{ shrink: true, sx: { fontFamily: 'Poppins', fontWeight: 600 } }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="standard">
                        <Typography sx={{ fontSize: '0.75rem', fontFamily: 'Poppins', color: '#64748b', mb: -0.5 }}>System Role</Typography>
                        <Select value={adminData.role} disabled sx={{ fontFamily: 'Roboto', fontSize: '0.95rem' }}>
                            <MenuItem value="Super Administrator">Super Administrator</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
        </Box>
    );

    const renderSecurity = () => (
        <Box sx={{ animation: 'fadeIn 0.5s ease' }}>
            <Typography variant="h6" sx={{ fontFamily: 'Poppins', fontWeight: 700, mb: 1 }}>Security & Privacy</Typography>
            <Stack spacing={3} mt={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography sx={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: '0.9rem' }}>Two-Factor Authentication</Typography>
                        <Typography sx={{ fontFamily: 'Roboto', fontSize: '0.8rem', color: '#64748b' }}>Secure your account with an extra layer of security.</Typography>
                    </Box>
                    <Switch checked={adminData.twoFactor} onChange={(e) => handleInputChange('twoFactor', e.target.checked)} />
                </Box>
                <Divider />
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography sx={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: '0.9rem' }}>Password Management</Typography>
                        <Typography sx={{ fontFamily: 'Roboto', fontSize: '0.8rem', color: '#64748b' }}>Update your login credentials regularly.</Typography>
                    </Box>
                    <Button 
                        variant="outlined" 
                        color="error" 
                        startIcon={<KeyIcon />} 
                        sx={vibrantButtonStyle}
                        onClick={() => setOpenPasswordModal(true)}
                    >
                        Update Password
                    </Button>
                </Box>
            </Stack>
        </Box>
    );

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#fff', p: { xs: 2, md: 5 }, mt: 8 }}>
            
            {/* Header */}
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={3} mb={6}>
                <Box position="relative">
                    <Avatar src={profileImage} sx={{ width: 100, height: 100, border: '4px solid #f1f5f9' }}>
                        {!profileImage && "AD"}
                    </Avatar>
                    <IconButton 
                        size="small" 
                        onClick={() => fileInputRef.current.click()}
                        sx={{ position: 'absolute', bottom: 0, right: 0, bgcolor: '#2563eb', color: '#fff', '&:hover': { bgcolor: '#1d4ed8' } }}
                    >
                        <CameraIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <input type="file" hidden ref={fileInputRef} accept="image/*" onChange={handleImageUpload} />
                </Box>
                <Box flexGrow={1}>
                    <Typography variant="h5" sx={{ fontFamily: 'Poppins', fontWeight: 800 }}>{adminData.fullName}</Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ShieldIcon sx={{ fontSize: 14, color: '#10b981' }} /> {adminData.role}
                    </Typography>
                    <Button variant="contained" disableElevation sx={{ ...vibrantButtonStyle, mt: 1, px: 4 }} onClick={handleSaveProfile}>
                        Save Profile
                    </Button>
                </Box>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Content Tabs */}
            <Grid container spacing={5}>
                <Grid item xs={12} md={3}>
                    <Stack spacing={1}>
                        {[
                            { id: 'profile', label: 'My Profile', icon: <PersonIcon sx={{ fontSize: 18 }} /> },
                            { id: 'security', label: 'Security', icon: <SecurityIcon sx={{ fontSize: 18 }} /> },
                            { id: 'notifications', label: 'Notifications', icon: <NotificationsIcon sx={{ fontSize: 18 }} /> },
                            { id: 'devices', label: 'Active Sessions', icon: <DevicesIcon sx={{ fontSize: 18 }} /> },
                        ].map((tab) => (
                            <Button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                startIcon={tab.icon}
                                sx={{
                                    justifyContent: 'flex-start', fontFamily: 'Poppins', fontSize: '0.85rem',
                                    fontWeight: activeTab === tab.id ? 700 : 500,
                                    color: activeTab === tab.id ? '#2563eb' : '#64748b',
                                    bgcolor: activeTab === tab.id ? '#eff6ff' : 'transparent',
                                    borderRadius: '8px', py: 1.2, px: 2
                                }}
                            >
                                {tab.label}
                            </Button>
                        ))}
                    </Stack>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Box sx={{ maxWidth: 700 }}>
                        {activeTab === 'profile' && renderProfile()}
                        {activeTab === 'security' && renderSecurity()}
                        {activeTab === 'notifications' && (
                            <Box sx={{ animation: 'fadeIn 0.5s ease' }}>
                                <Typography variant="h6" sx={{ fontFamily: 'Poppins', fontWeight: 700, mb: 3 }}>Alert Preferences</Typography>
                                <Stack spacing={3}>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography>Receive email reports daily</Typography>
                                        <Switch checked={adminData.emailAlerts} color="info" />
                                    </Box>
                                </Stack>
                            </Box>
                        )}
                        {activeTab === 'devices' && (
                            <Box sx={{ animation: 'fadeIn 0.5s ease' }}>
                                <Typography variant="h6" sx={{ fontFamily: 'Poppins', fontWeight: 700, mb: 2 }}>Active Sessions</Typography>
                                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <DevicesIcon color="primary" />
                                    <Box flexGrow={1}>
                                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Windows PC - Chrome Browser</Typography>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>Patna, India • Active Now</Typography>
                                    </Box>
                                    <Chip label="Current" size="small" sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700 }} />
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>

            {/* --- CHANGE PASSWORD MODAL --- */}
            <Dialog 
                open={openPasswordModal} 
                onClose={() => setOpenPasswordModal(false)}
                PaperProps={{ sx: { borderRadius: '12px', width: '100%', maxWidth: '400px' } }}
            >
                <DialogTitle sx={{ fontFamily: 'Poppins', fontWeight: 700 }}>Update Password</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} mt={1}>
                        <TextField
                            fullWidth label="Current Password"
                            type={showPasswords ? "text" : "password"}
                            variant="outlined"
                            value={passwordData.currentPassword}
                            onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
                        />
                        <TextField
                            fullWidth label="New Password"
                            type={showPasswords ? "text" : "password"}
                            variant="outlined"
                            value={passwordData.newPassword}
                            onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                        />
                        <TextField
                            fullWidth label="Confirm New Password"
                            type={showPasswords ? "text" : "password"}
                            variant="outlined"
                            value={passwordData.confirmPassword}
                            onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                        />
                        <Button 
                            size="small" 
                            startIcon={showPasswords ? <VisibilityOff /> : <Visibility />}
                            onClick={() => setShowPasswords(!showPasswords)}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none', color: '#64748b' }}
                        >
                            {showPasswords ? "Hide Passwords" : "Show Passwords"}
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenPasswordModal(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        onClick={handleUpdatePassword}
                        sx={{ bgcolor: '#2563eb', fontWeight: 600 }}
                    >
                        Update Password
                    </Button>
                </DialogActions>
            </Dialog>

            <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }`}</style>
        </Box>
    );
};

export default AdminProfileSettings;